import { useMemo } from "react";

import { groupBy, orderBy, sumBy } from "lodash";
import moment from "moment";

import { TrackPerformanceTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { usePerformanceTrackingSpendData } from "../hooks/use-performance-tracking-spend-data";
import { useTableSort } from "../hooks/use-table-sort";

import {
  Table,
  WeekStatus,
  type TableRowType,
} from "./components/performance-tracking-table";
import { TableSorter, sortControl } from "./components/table-sorter";

type WeeklyMetrics = {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  actual: number | null;
  budgeted: number | null;
}[];

function getWeekStatusForDate(
  weekStart: moment.Moment,
  weekEnd: moment.Moment,
  referenceDate: moment.Moment,
): WeekStatus {
  if (weekEnd.isBefore(referenceDate)) {
    return WeekStatus.COMPLETE;
  }
  if (weekStart.isSameOrBefore(referenceDate)) {
    return WeekStatus.CURRENT;
  } else {
    return WeekStatus.UPCOMING;
  }
}

const aggregateWeeklyMetrics = (
  rows: {
    weeklyMetrics: WeeklyMetrics;
    [key: string]: any;
  }[],
  weeksDiff: number,
) =>
  Array.from({ length: weeksDiff }).map((_, i) => {
    const weekSum = {
      actual: 0,
      budgeted: 0,
      dateRange: {
        startDate: "",
        endDate: "",
      },
      status: WeekStatus.COMPLETE,
    };
    const today = moment().startOf("day");

    rows.forEach((row) => {
      const week = row.weeklyMetrics[i];
      weekSum.actual += week.actual ?? 0;
      weekSum.budgeted += week.budgeted ?? 0;
      weekSum.dateRange = week.dateRange;
      const weekEnd = moment(weekSum.dateRange.endDate);
      const weekStart = moment(weekSum.dateRange.startDate);
      weekSum.status = getWeekStatusForDate(weekStart, weekEnd, today);
    });

    return weekSum;
  });

export function PerformanceTrackingTable() {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.TRACK_PERFORMANCE);
  const { trackingSpendData, loading } = usePerformanceTrackingSpendData();
  const [sort, setSort] = useTableSort();
  const { selectedCycle } = useSelectedCycle();
  const numberOfBreakdowns = selectedCycle.breakdowns.filter(
    (breakdown: string[]) => breakdown.length > 0,
  ).length;

  const weeks = useMemo(() => {
    const settingStartDate = moment(selectedCycle.date_range.start_date);
    const settingEndDate = moment(selectedCycle.date_range.end_date);
    const weeksDiff = settingEndDate.diff(settingStartDate, "weeks") + 1;
    return Array.from({ length: weeksDiff }).map((_, i) => {
      return {
        dateRange: {
          startDate: settingStartDate
            .clone()
            .add(i, "weeks")
            .format("YYYY-MM-DD"),
          endDate: settingStartDate
            .clone()
            .add(i, "weeks")
            .add(6, "days")
            .format("YYYY-MM-DD"),
        },
      };
    });
  }, [selectedCycle.date_range]);

  const trackingSpendDataWeekly = useMemo(() => {
    if (loading) {
      return [];
    }
    return trackingSpendData.map((datum) => {
      const weeklyMetricsByStart: Record<string, WeeklyMetrics[number]> = {};
      datum.dailyMetrics.forEach((dailydatum) => {
        // get week
        const weekStart = weeks.find(
          (i) =>
            moment(dailydatum.date).isSameOrAfter(i.dateRange.startDate) &&
            moment(dailydatum.date).isSameOrBefore(i.dateRange.endDate),
        );
        if (!weekStart) {
          console.error(
            `Daily datum in tracking page not present: ${dailydatum.date}`,
          );
          return;
        }
        const start = weekStart.dateRange.startDate;
        if (!weeklyMetricsByStart[start]) {
          weeklyMetricsByStart[start] = {
            dateRange: weekStart.dateRange,
            actual: 0,
            budgeted: 0,
          };
        }

        weeklyMetricsByStart[start].budgeted! += dailydatum.budgeted ?? 0;
        weeklyMetricsByStart[start].actual! += dailydatum.actual ?? 0;
      });

      const ordered = orderBy(
        Object.values(weeklyMetricsByStart),
        "dateRange.startDate",
        "asc",
      );

      return {
        ...datum,
        weeklyMetrics: ordered,
      };
    });
  }, [trackingSpendData, weeks, loading]);

  let countRows = 0;
  // this is guaranteed
  const firstBreakdown = selectedCycle.breakdowns[0];
  const hasMultipleBreakdowns = numberOfBreakdowns > 1;
  const rows = useMemo<TableRowType[]>(() => {
    if (loading) {
      return [];
    }
    const tableRows = Object.entries(
      groupBy(trackingSpendDataWeekly, (e) => e.breakdowns[firstBreakdown]),
    ).map(([key, children], parentIndex) => {
      const parentKey = `{"${firstBreakdown}":"${key}"}`;

      const actualSpend = sumBy(children, (e) => e.actualSpend);
      const mtaRevenue = sumBy(children, (e) => e.mtaRevenue);
      const mtaTransactions = sumBy(children, (e) => e.mtaTransactions);

      const today = moment().endOf("day");

      let budgetedSpendToDate = 0;
      children.forEach((child) =>
        child.dailyMetrics.forEach((dailyMetric) => {
          if (moment(dailyMetric.date).isSameOrBefore(today)) {
            budgetedSpendToDate += dailyMetric.budgeted ?? 0;
          }
        }),
      );

      return {
        id: parentKey,
        key: parentKey,
        index: parentIndex,
        data: {
          weeklyMetrics: aggregateWeeklyMetrics(children, weeks.length),
          actualSpend,
          actualSpendMix: sumBy(children, (e) => e.actualSpendMix),
          budgetedSpend: sumBy(children, (e) => e.budgetedSpend),
          budgetedSpendMix: sumBy(children, (e) => e.budgetedSpendMix),
          budgetedSpendToDate,
          mtaRevenue,
          mtaTransactions,
          mtaRoas: mtaRevenue / actualSpend,
          mtaCac: actualSpend / mtaTransactions,
        },
        metadata: {
          breakdowns: { [firstBreakdown]: key },
          currentBreakdown: {
            key: firstBreakdown,
            value: key,
          },
        },
        rowType: "normal" as const,
        hasChildren: hasMultipleBreakdowns,
        children: !hasMultipleBreakdowns
          ? []
          : children.map((e) => {
              return {
                id: e.key,
                key: e.key,
                index: countRows++,
                data: {
                  ...e,
                  weeklyMetrics: e.weeklyMetrics.map((metric) => {
                    const weekEnd = moment(metric.dateRange.endDate);
                    const weekStart = moment(metric.dateRange.startDate);
                    const status = getWeekStatusForDate(
                      weekStart,
                      weekEnd,
                      today,
                    );
                    return {
                      ...metric,
                      status,
                    };
                  }),
                  spend: e.weeklyMetrics.reduce(
                    (acc, metric) => acc + (metric.actual || 0),
                    0,
                  ),
                  budgetedSpendToDate: e.dailyMetrics.reduce(
                    (acc, metric) =>
                      acc +
                      (moment(metric.date).isSameOrBefore(today)
                        ? metric.budgeted ?? 0
                        : 0),
                    0,
                  ),
                },
                metadata: {
                  breakdowns: e.breakdowns,
                  currentBreakdown: {
                    key: Object.keys(e.breakdowns)[1],
                    value: Object.values(e.breakdowns)[1] as string,
                  },
                },
                children: [],
                hasChildren: false,
                rowType: "normal" as const,
              };
            }),
      };
    });

    return sortControl(sort)(tableRows);
  }, [
    trackingSpendDataWeekly,
    sort,
    firstBreakdown,
    hasMultipleBreakdowns,
    countRows,
    weeks.length,
    loading,
  ]);

  const HEADING_ROW = useMemo<TableRowType>(() => {
    const actualSpend = sumBy(rows, (e) => e.data.actualSpend);
    const mtaTransactions = sumBy(rows, (e) => e.data.mtaTransactions);
    const mtaRevenue = sumBy(rows, (e) => e.data.mtaRevenue);

    let budgetedSpendToDate = 0;
    rows.forEach((row) => {
      budgetedSpendToDate += row.data.budgetedSpendToDate ?? 0;
    });

    return {
      id: "heading",
      key: "heading",
      isHeaderRow: true,
      index: 0,
      data: {
        weeklyMetrics: aggregateWeeklyMetrics(
          rows.map((e) => e.data),
          weeks.length,
        ),
        actualSpend,
        actualSpendMix: sumBy(rows, (e) => e.data.actualSpendMix),
        budgetedSpend: sumBy(rows, (e) => e.data.budgetedSpend),
        budgetedSpendMix: sumBy(rows, (e) => e.data.budgetedSpendMix),
        budgetedSpendToDate: budgetedSpendToDate,
        mtaRevenue,
        mtaTransactions,
        mtaRoas: mtaRevenue / actualSpend,
        mtaCac: actualSpend / mtaTransactions,
      },
      metadata: {
        breakdowns: {},
        currentBreakdown: { key: "", value: "" },
      },
      children: [],
      hasChildren: false,
      rowType: "normal",
    };
  }, [rows, weeks.length]);

  return (
    <>
      <TableSorter
        sort={sort}
        setSort={(value) => {
          trackMMMEvent({
            component:
              TrackPerformanceTrackedComponent.PERFORMANCE_TRACKING_TABLE,
            action: "onChange",
            actionInfo: "Change table sort",
            data: getMMMEventSelectionData(value),
          });

          setSort(value);
        }}
      />
      <Table
        headingRow={HEADING_ROW}
        loading={loading}
        rows={rows}
        maxIndentLevel={numberOfBreakdowns - 1}
      />
    </>
  );
}
